<template>
  <div id="js-qubl"></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, reactive, ref } from "vue"

import { useExitModal } from "@/composables/useExitModal"

import { GlobalFunctions } from "@/helpers/globalFunctions"

import GtmService, { UserFlow } from "@/services/gtmService"
import QuizService from "@/services/quizService"

import type { QuizData } from "@/types/quizData"

// EMITS
const emit = defineEmits(["quizCompleted", "quizSuccess", "quizError"])

// COMPOSABLES
const { removeExitModal } = useExitModal()

// PROPS

const props = defineProps({
  quizId: {
    type: String,
    required: true
  },
  quizStyle: {
    type: String,
    required: true
  }
})

// DATA

const data = reactive({
  quizData: {} as QuizData
})

// FUNCTIONS (QUBL SETUP)

function listenToQublPostback(quizId: string): void {
  const qublInstance = document.querySelector("qubl-instance")

  if (!qublInstance) {
    console.error("Qubl instance not found")
    return
  }

  qublInstance.addEventListener("create", async () => {
    GtmService.quizStartEvent(UserFlow.REPORT)
  })

  qublInstance.addEventListener("postback", async (qublResponse: any) => {
    await GtmService.quizCompleteEvent(UserFlow.REPORT)

    data.quizData = qublResponse?.detail
    processQuizCompletion(data.quizData)

    // Remove quiz data in localstorage to avoid quiz retake blank screen
    localStorage.removeItem(quizId)
  })

  qublInstance.addEventListener("next", async () => {
    observeHelperText()
  })

  qublInstance.addEventListener("back", async () => {
    observeHelperText()
  })
}

const observer = ref<MutationObserver | null>(null)

const observeHelperText = () => {
  const qublInstance = document.querySelector("qubl-instance")?.shadowRoot
  const qublQuiz = qublInstance?.querySelector("qubl-quiz")?.shadowRoot

  if (qublQuiz && !observer.value) {
    observer.value = new MutationObserver((mutationsList) => {
      for (let mutation of mutationsList) {
        if (mutation.type === "childList") {
          const questionContainer = qublQuiz?.querySelector(".question-container")
          const helperText = qublQuiz.querySelector("#helper-text:not(#helper-text.moved)")

          if (helperText) {
            if (questionContainer) {
              helperText.classList.add("moved")
              questionContainer.appendChild(helperText)
            }
          }
        }
      }
    })

    observer.value.observe(qublQuiz, { childList: true, subtree: true })
  }
}

const unobserveHelperText = () => {
  if (observer.value) {
    observer.value.disconnect()
  }
}

// FUNCTIONS (QUIZ COMPLETION)

async function processQuizCompletion(quizData: QuizData): Promise<void> {
  // Event triggers UI update
  emit("quizCompleted")

  // Exit modal no longer needed
  removeExitModal()

  try {
    // Collect the quiz data
    const { quizUid: qublQuizUuid, instanceUid: qublInstanceUuid, answers, results } = quizData

    // Collect the user data
    let userSource = GlobalFunctions.getUserSourceData() || {}
    userSource["quiz_page"] = window.location.pathname
    const user = {
      fullName: answers.fullName,
      phoneNumber: answers.phoneNumber,
      gender: answers.gender,
      zipCode: answers.zipCode,
      userSource
    }

    // Remove user variables from answers
    const cleanedAnswers = QuizService.removeUserVariablesFromAnswers(JSON.parse(JSON.stringify(answers)))
    const quiz = {
      qublQuizUuid,
      qublInstanceUuid,
      ...QuizService.getRiasecFromResults(results),
      ...cleanedAnswers
    }

    // Store the user and quiz data in local storage to be retrieved after oauth
    sessionStorage.setItem("userData", JSON.stringify(user))
    sessionStorage.setItem("quizData", JSON.stringify(quiz))

    emit("quizSuccess", {
      full_name: answers.fullName,
      zip_code: answers.zipCode,
      test_purpose: answers.testTakingPurpose
    })
  } catch (error) {
    emit("quizError", error, quizData)
  }
}

// Lifecycle
onMounted(() => {
  GlobalFunctions.storeUserSourceData()
  QuizService.loadQublInstance(props.quizId, props.quizStyle)
  listenToQublPostback(props.quizId)
})

onUnmounted(() => {
  unobserveHelperText()
})
</script>
